/**
 * Card Meta Ads — status no próprio card; configurar recursos reusa MetaIntegrationModal
 * via OAuth (connect/reconnect). Sem segundo modal.
 */
import React from 'react';
import { AppIcon } from '../../../components/icons/AppIcon';
import { MetaLogoIcon } from '../../../components/icons/CustomIcons';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import MetaConnectionSummary from './MetaConnectionSummary';
import { getMetaTokenStatus } from '../types/meta';

interface MetaConnectionState {
    connecting: boolean;
    connected: boolean;
    accountName?: string;
    pixelName?: string;
    pageName?: string;
    expiresAt?: string;
}

interface MetaAdsCardProps {
    state: MetaConnectionState;
    onConnect: () => void;
    onDisconnect: () => void;
    onReconnect: () => void;
    formatExpiryDate: (dateString?: string) => string | null;
    /** Sem Card externo — uso em onboarding. */
    embedded?: boolean;
}

const MetaAdsCard: React.FC<MetaAdsCardProps> = ({
    state,
    onConnect,
    onDisconnect,
    onReconnect,
    formatExpiryDate,
    embedded = false,
}) => {
    const body = (
        <>
            <header className="mb-4 flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                    <MetaLogoIcon className="size-9 shrink-0 sm:size-10" />
                    <h3 className="text-section-title text-default">Meta Ads</h3>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    {state.connected ? (
                        <>
                            <span className="rounded-full bg-success/10 px-3 py-1 text-2xs font-medium text-success">
                                Conectado
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="min-h-11 min-w-11 text-muted hover:text-default sm:min-h-9 sm:min-w-9"
                                onClick={onReconnect}
                                disabled={state.connecting}
                                aria-label="Alterar recursos do Meta Ads"
                                title="Alterar conta, pixel ou página"
                            >
                                <AppIcon name="settings" className="size-5" aria-hidden />
                            </Button>
                        </>
                    ) : (
                        <span className="rounded-full bg-surface-subtle px-3 py-1 text-2xs font-medium text-muted">
                            Não conectado
                        </span>
                    )}
                </div>
            </header>

            {state.connected ? (
                <ConnectedContent
                    state={state}
                    onReconnect={onReconnect}
                    onDisconnect={onDisconnect}
                    formatExpiryDate={formatExpiryDate}
                />
            ) : (
                <DisconnectedContent
                    connecting={state.connecting}
                    onConnect={onConnect}
                />
            )}
        </>
    );

    if (embedded) {
        return <div className="space-y-1">{body}</div>;
    }

    return (
        <Card>
            <Card.Content>{body}</Card.Content>
        </Card>
    );
};

interface ConnectedContentProps {
    state: MetaConnectionState;
    onReconnect: () => void;
    onDisconnect: () => void;
    formatExpiryDate: (dateString?: string) => string | null;
}

const ConnectedContent: React.FC<ConnectedContentProps> = ({
    state,
    onReconnect,
    onDisconnect,
    formatExpiryDate,
}) => {
    const tokenStatus = getMetaTokenStatus(state.expiresAt);
    const isExpired = tokenStatus === 'expired';
    const isExpiring = tokenStatus === 'expiring';

    return (
        <>
            {isExpired ? (
                <aside
                    className="mb-4 rounded-lg border border-error/20 bg-error/10 p-3"
                    role="alert"
                >
                    <div className="flex items-start gap-2">
                        <AppIcon name="alert" className="mt-0.5 size-5 shrink-0 text-error" aria-hidden />
                        <div>
                            <p className="text-sm font-medium text-error">Token expirado</p>
                            <p className="mt-1 text-xs text-error/80">
                                Reconecte no Meta para continuar rastreando leads.
                            </p>
                        </div>
                    </div>
                </aside>
            ) : null}

            {isExpiring ? (
                <aside
                    className="mb-4 rounded-lg border border-warning/20 bg-warning/10 p-3"
                    role="status"
                >
                    <div className="flex items-start gap-2">
                        <AppIcon
                            name="alert-triangle"
                            className="mt-0.5 size-5 shrink-0 text-warning"
                            aria-hidden
                        />
                        <div>
                            <p className="text-sm font-medium text-warning">Token expirando</p>
                            <p className="mt-1 text-xs text-warning/80">
                                Reconecte em breve para evitar interrupções.
                            </p>
                        </div>
                    </div>
                </aside>
            ) : null}

            <div className="mb-4">
                <MetaConnectionSummary data={state} formatExpiryDate={formatExpiryDate} />
            </div>

            {!isExpired ? (
                <aside className="mb-4 rounded-lg border border-success/20 bg-success/10 p-3">
                    <p className="mb-1 text-xs font-medium text-success">Rastreamento ativo</p>
                    <p className="text-xs text-success/90">
                        Leads de Click-to-WhatsApp entram com campanha, conjunto e anúncio.
                    </p>
                </aside>
            ) : null}

            <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                    type="button"
                    variant="secondary"
                    className="min-h-11 w-full touch-manipulation sm:min-h-9"
                    onClick={onDisconnect}
                    disabled={state.connecting}
                >
                    <AppIcon name="unlink" className="size-4" aria-hidden />
                    Desconectar
                </Button>
                <Button
                    type="button"
                    variant={isExpired ? 'default' : 'secondary'}
                    className="min-h-11 w-full touch-manipulation sm:min-h-9"
                    onClick={onReconnect}
                    disabled={state.connecting}
                >
                    {state.connecting ? (
                        <>
                            <AppIcon name="loader" className="size-4 animate-spin" aria-hidden />
                            Abrindo Meta…
                        </>
                    ) : (
                        <>
                            <AppIcon name="refresh" className="size-4" aria-hidden />
                            {isExpired ? 'Reconectar' : 'Alterar recursos'}
                        </>
                    )}
                </Button>
            </div>
        </>
    );
};

interface DisconnectedContentProps {
    connecting: boolean;
    onConnect: () => void;
}

const DisconnectedContent: React.FC<DisconnectedContentProps> = ({
    connecting,
    onConnect,
}) => (
    <>
        <p className="mb-4 text-body text-secondary">
            Conecte o Meta Ads para atribuir leads de Click-to-WhatsApp à campanha certa.
        </p>

        <ul className="mb-6 space-y-2 text-sm text-secondary">
            <li className="flex items-center gap-2">
                <AppIcon name="check" className="size-4 shrink-0 text-success" aria-hidden />
                Identifica campanha, conjunto e anúncio
            </li>
            <li className="flex items-center gap-2">
                <AppIcon name="check" className="size-4 shrink-0 text-success" aria-hidden />
                Envia eventos Lead e Purchase
            </li>
            <li className="flex items-center gap-2">
                <AppIcon name="check" className="size-4 shrink-0 text-success" aria-hidden />
                Ajuda a otimizar campanhas com dados reais
            </li>
        </ul>

        <Button
            type="button"
            className="min-h-11 w-full touch-manipulation sm:min-h-9"
            onClick={onConnect}
            disabled={connecting}
        >
            {connecting ? (
                <>
                    <AppIcon name="loader" className="size-4 animate-spin" aria-hidden />
                    Abrindo Meta…
                </>
            ) : (
                <>
                    <MetaLogoIcon className="size-4" aria-hidden />
                    Conectar Meta Ads
                </>
            )}
        </Button>
    </>
);

export default MetaAdsCard;
